import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ToastContainer } from '../components/Toast';

interface Toast {
  id: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  icon?: string | React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, 'id'>) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (toast: Omit<Toast, 'id'>) => {
    const id = Date.now().toString();
    const newToast = { ...toast, id };
    setToasts(prev => [...prev, newToast]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const showSuccess = (message: string) => {
    showToast({ message, type: 'success' });
  };

  const showError = (message: string) => {
    showToast({ message, type: 'error' });
  };

  const showInfo = (message: string) => {
    showToast({ message, type: 'info' });
  };

  const showWarning = (message: string) => {
    showToast({ message, type: 'warning' });
  };

  const toastProps = toasts.map(toast => ({
    ...toast,
    onClose: removeToast
  }));

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showInfo, showWarning }}>
      {children}
      <ToastContainer toasts={toastProps} />
    </ToastContext.Provider>
  );
};